import { useState, useMemo, useRef, useEffect } from "react";
import { format } from "date-fns";
import { Plus, FileText, Search, Trash2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  CollaborativeEditor,
  CollaborativeEditorRef,
} from "@/components/editors/CollaborativeEditor";
import {
  StudioDocument,
  useStudioDocuments,
  useCreateStudioDocument,
  useUpdateStudioDocument,
  useDeleteStudioDocument,
} from "@/hooks/useStudioDocuments";
import type { Tables } from "@/integrations/neon/types";

interface StudioDocumentsTabProps {
  studio: Tables<"studios">;
}

function StudioDocumentCard({
  doc,
  initialEditing = false,
}: {
  doc: StudioDocument;
  initialEditing?: boolean;
}) {
  const [title, setTitle] = useState(doc.title);
  const [editorOpen, setEditorOpen] = useState(initialEditing);
  const editorRef = useRef<CollaborativeEditorRef>(null);
  const updateDoc = useUpdateStudioDocument();
  const deleteDoc = useDeleteStudioDocument();

  // Sync if server data changes
  useEffect(() => {
    setTitle(doc.title);
  }, [doc.title]);

  const handleTitleBlur = () => {
    const trimmed = title.trim();
    if (trimmed && trimmed !== doc.title) {
      updateDoc.mutate({ id: doc.id, title: trimmed });
    }
  };

  const handleDelete = () => {
    deleteDoc.mutate(doc.id);
  };

  return (
    <Card id={`studio-doc-${doc.id}`} className="scroll-mt-24">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <button
            type="button"
            onClick={() => setEditorOpen((o) => !o)}
            className="mt-2 shrink-0"
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform",
                editorOpen && "rotate-90",
              )}
            />
          </button>
          <div className="flex-1 space-y-2">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleBlur}
              className="text-xl font-semibold border-transparent hover:border-input focus:border-input transition-colors"
              placeholder="Document title"
              autoFocus={initialEditing}
            />
            <span className="text-xs text-muted-foreground">
              Updated {format(new Date(doc.updated_at), "dd.MM.yyyy HH:mm")}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete document?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the documentation page.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>

      {editorOpen && (
        <CardContent>
          <CollaborativeEditor
            ref={editorRef}
            documentId={doc.document_id}
            editable={true}
            placeholder="Start writing documentation..."
          />
        </CardContent>
      )}
    </Card>
  );
}

export function StudioDocumentsTab({ studio }: StudioDocumentsTabProps) {
  const { data: docs = [], isLoading } = useStudioDocuments(studio.id);
  const createDoc = useCreateStudioDocument();
  const [search, setSearch] = useState("");
  const [newDocId, setNewDocId] = useState<string | null>(null);

  const handleCreateDoc = () => {
    createDoc.mutate(
      {
        title: "Untitled Document",
        studio_id: studio.id,
      },
      {
        onSuccess: (doc) => {
          setNewDocId(doc.id);
          setTimeout(() => {
            const element = document.getElementById(`studio-doc-${doc.id}`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 100);
        },
      },
    );
  };

  const filteredDocs = useMemo(() => {
    if (!search.trim()) return docs;
    const q = search.toLowerCase();
    return docs.filter((d) => d.title.toLowerCase().includes(q));
  }, [docs, search]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with search and create */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-1">
          <h2 className="text-lg font-semibold">Docs</h2>
          {docs.length > 0 && (
            <div className="relative max-w-xs flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search pages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          )}
        </div>
        <Button onClick={handleCreateDoc} disabled={createDoc.isPending}>
          <Plus className="h-4 w-4 mr-2" />
          {createDoc.isPending ? "Creating..." : "New Page"}
        </Button>
      </div>

      {/* Documents list */}
      {filteredDocs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <CardTitle className="text-lg mb-1">
              {search ? "No matching pages" : "No documentation yet"}
            </CardTitle>
            <p className="text-muted-foreground text-sm max-w-sm">
              {search
                ? "Try a different search term."
                : "Create your first documentation page to capture knowledge about this studio."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredDocs.map((doc) => (
            <StudioDocumentCard
              key={doc.id}
              doc={doc}
              initialEditing={doc.id === newDocId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
